import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

const stats = [
  { value: '8000', unit: 'MT', label: 'Annual waste collection capacity' },
  { value: '88%', unit: '', label: 'Average material recovery yield' },
  { value: '5000+', unit: 'T', label: 'Recovered material target 2025' },
  { value: '15+', unit: '', label: 'Commercial partners' },
];

const tickerItems = [
  'Plastic diverted from landfills',
  'Paper & cardboard recycled',
  'Metals returned to manufacturing',
  'E-waste responsibly processed',
  'Expired stock safely disposed',
  'EPR compliance delivered',
  'ESG data for sustainability reports',
  'Zero untreated waste to landfill',
];

// Gallery images for the strip
const galleryImages = [
  { src: '/images/gallery-01.webp', alt: 'Waste collection operations' },
  { src: '/images/gallery-05.webp', alt: 'Material segregation' },
  { src: '/images/gallery-09.webp', alt: 'Recycling facility' },
  { src: '/images/gallery-16.webp', alt: 'Resource recovery' },
  { src: '/images/gallery-21.webp', alt: 'Community initiative' },
  { src: '/images/gallery-02.webp', alt: 'Waste processing' },
];

export function Impact() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <section ref={ref} id="impact" className="py-20 lg:py-28 bg-gray-950 text-white overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="mb-16"
        >
          <p className="text-xs font-semibold tracking-widest uppercase text-gray-500 mb-4">
            Impact & Sustainability
          </p>
          <h2 className="text-4xl sm:text-5xl font-bold text-white max-w-2xl">
            Circular systems create measurable impact.
          </h2>
        </motion.div>

        {/* Stats row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 mb-16">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.4, delay: index * 0.08 }}
              className={`py-8 ${index > 0 ? 'lg:border-l lg:border-gray-800 lg:pl-8' : ''}`}
            >
              <div className="text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-1 tabular-nums">
                {stat.value}
                {stat.unit && <span className="text-p-500 text-3xl lg:text-4xl ml-1">{stat.unit}</span>}
              </div>
              <p className="text-sm text-gray-400">{stat.label}</p>
            </motion.div>
          ))}
        </div>

        {/* 2025 Targets */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mb-16 p-6 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl"
        >
          <p className="text-xs font-semibold tracking-widest uppercase text-p-500 mb-4">2025 Impact Targets</p>
          <div className="grid sm:grid-cols-3 gap-4 text-sm text-gray-300">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-p-500 shrink-0" />
              5000+ tonnes of recovered material annually
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-p-500 shrink-0" />
              Zero untreated waste to landfill
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-p-500 shrink-0" />
              15+ commercial partnerships
            </div>
          </div>
        </motion.div>
      </div>

      {/* Gallery strip */}
      <div className="overflow-hidden mb-8">
        <div className="ticker-track gap-3">
          {[...galleryImages, ...galleryImages].map((img, i) => (
            <div key={i} className="ticker-item mx-1.5 shrink-0">
              <picture>
                <source srcSet={img.src} type="image/webp" />
                <img
                  src={img.src}
                  alt={img.alt}
                  width={320}
                  height={213}
                  className="w-72 h-48 object-cover rounded-xl"
                  loading="lazy"
                />
              </picture>
            </div>
          ))}
        </div>
      </div>

      {/* Ticker */}
      <div className="border-t border-gray-800 pt-8 overflow-hidden">
        <div className="ticker-track">
          {[...tickerItems, ...tickerItems].map((item, i) => (
            <span key={i} className="ticker-item text-sm text-gray-500">
              {item}
              <span className="mx-6 text-gray-700">•</span>
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
